<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { onMounted, ref, watch } from 'vue';
import { useGlobalProperties } from '@/src/composables/useGlobalProperties';
import { usersStore } from '@/src/store/users';
import SeSwitch from '@/src/ui-kit/switch/Switch.vue';
import SeIcon from '@/src/ui-kit/icon/Icon.vue';
import SeButton from '@/src/ui-kit/button/Button.vue';
import { theme } from '@/theme';

const users = usersStore();
const { currentUser, isAdvisor } = storeToRefs(users);
const { changeOnlineStatus } = users;

const { getGlobalProperties } = useGlobalProperties();
const { $chatSound, $route } = getGlobalProperties();

const { siteActive } = theme.general;

onMounted(() => {
  if ($route.path.includes('chat') || $route.path.includes('inbox')) {
    document.getElementById('quickSettingsContainer')?.classList.add('hidden');
  }
});

watch(
  () => currentUser.value.status,
  (newStatus) => {
    statusSwitch.value = newStatus !== 'offline';
  }
);

const statusSwitch = ref(currentUser.value.status !== 'offline');
const testSoundPlaying = ref(false);

const testSound = () => {
  testSoundPlaying.value = true;
  $chatSound.play();
};

const stopSound = () => {
  testSoundPlaying.value = false;
  $chatSound.stop();
};

const updateAdvisorStatus = () => {
  const status = statusSwitch.value ? 'online' : 'offline';
  changeOnlineStatus(status).finally(() => {
    statusSwitch.value = currentUser.value.status !== 'offline';
  });
};
</script>

<script lang="ts">
export default {
  name: 'SeAdvisorQuickSettings',
};
</script>

<template>
  <div
    v-if="siteActive && isAdvisor && currentUser?.account_status !== 'inactive'"
    id="quickSettingsContainer"
    class="quick-settings-container"
  >
    <div class="status-container">
      <p class="flex flex-col text-b-xs">
        <span class="text-neutral-60">Status:</span>
        <span
          class="text-neutral-100 font-bold capitalize"
          :class="{
            'text-success-100': currentUser.status === 'online',
            'text-neutral-60': currentUser.status === 'offline',
            'text-warning-100': currentUser.status === 'busy',
          }"
        >
          {{ currentUser.status }}
        </span>
      </p>
      <se-switch
        v-model="statusSwitch"
        @update:model-value="updateAdvisorStatus"
        :disabled="currentUser.status === 'busy'"
        size="small"
      />
    </div>
    <div class="chat-modes-container">
      <p class="text-neutral-60 text-b-xs mb-1">Chat Modes:</p>
      <div class="flex items-center justify-between">
        <se-icon name="video" :class="currentUser.video_enabled ? 'text-love-100' : 'text-neutral-50'" />
        <se-icon name="phone-call" :class="currentUser.audio_enabled ? 'text-feature-100' : 'text-neutral-50'" />
        <se-icon name="message-circle" :class="currentUser.text_enabled ? 'text-primary-100' : 'text-neutral-50'" />
      </div>
    </div>
    <template v-if="currentUser.video_enabled || currentUser.audio_enabled">
      <se-button v-if="!testSoundPlaying" @click="testSound" type="tertiary" size="small" class="w-full">
        Sound Test
      </se-button>
      <se-button v-else @click="stopSound" type="error" size="small" class="w-full">Stop Test</se-button>
    </template>
  </div>
</template>

<style scoped lang="scss">
.quick-settings-container {
  @apply w-[120px] fixed right-0 bottom-[83px] p-8 rounded-l-12 z-[100] bg-neutral-0 border border-neutral-30;
}

.status-container {
  @apply flex items-center justify-between bg-neutral-20 rounded-8 p-8 mb-8 w-full;
}

.chat-modes-container {
  @apply bg-neutral-20 rounded-8 p-8 mb-8 w-full;
}
</style>
